class WebGameApi {
  constructor(canvasId = 'game-canvas') {
    this.canvasId = canvasId
    this.canvas = null
    this.activePointerId = null
    this.touchHandlers = {
      start: [],
      move: [],
      end: []
    }
  }

  createCanvas() {
    if (this.canvas) return this.canvas
    const canvas = document.getElementById(this.canvasId)
    if (!(canvas instanceof HTMLCanvasElement)) {
      throw new Error(`Canvas #${this.canvasId} was not found`)
    }
    this.canvas = canvas
    this.bindPointerEvents()
    return canvas
  }

  getSystemInfoSync() {
    const canvas = this.createCanvas()
    const stage = canvas.parentElement
    const rect = stage?.getBoundingClientRect() ?? canvas.getBoundingClientRect()
    const windowWidth = Math.max(1, Math.floor(rect.width))
    const windowHeight = Math.max(1, Math.floor(rect.height))
    return {
      windowWidth,
      windowHeight,
      pixelRatio: window.devicePixelRatio || 1,
      safeArea: {
        top: 0,
        right: windowWidth,
        bottom: windowHeight,
        left: 0,
        width: windowWidth,
        height: windowHeight
      }
    }
  }

  onTouchStart(handler) {
    this.touchHandlers.start.push(handler)
  }

  onTouchMove(handler) {
    this.touchHandlers.move.push(handler)
  }

  onTouchEnd(handler) {
    this.touchHandlers.end.push(handler)
  }

  onWindowResize(handler) {
    let scheduled = false
    const schedule = () => {
      if (scheduled) return
      scheduled = true
      requestAnimationFrame(() => {
        scheduled = false
        handler()
      })
    }
    window.addEventListener('resize', schedule, { passive: true })
    window.addEventListener('orientationchange', schedule, { passive: true })
    if (typeof ResizeObserver === 'function' && this.canvas?.parentElement) {
      const observer = new ResizeObserver(schedule)
      observer.observe(this.canvas.parentElement)
      this.resizeObserver = observer
    }
  }

  async share(message = {}) {
    const title = message.title || document.title
    const url = new URL(window.location.href)
    if (message.query) url.search = message.query

    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title, text: title, url: url.toString() })
        return { ok: true, platform: 'web', method: 'share' }
      } catch (error) {
        if (error?.name === 'AbortError') {
          return { ok: false, platform: 'web', reason: 'cancelled' }
        }
        return { ok: false, platform: 'web', reason: 'share_failed', error }
      }
    }

    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(`${title} ${url}`)
        return { ok: true, platform: 'web', method: 'clipboard' }
      } catch (error) {
        return { ok: false, platform: 'web', reason: 'clipboard_failed', error }
      }
    }

    return { ok: false, platform: 'web', reason: 'not_supported' }
  }

  bindPointerEvents() {
    const canvas = this.canvas
    const emit = (type, event) => {
      const rect = canvas.getBoundingClientRect()
      const scaleX = rect.width > 0 ? canvas.width / rect.width : 1
      const scaleY = rect.height > 0 ? canvas.height / rect.height : 1
      const touch = {
        identifier: event.pointerId,
        clientX: (event.clientX - rect.left) * scaleX,
        clientY: (event.clientY - rect.top) * scaleY
      }
      const payload = { changedTouches: [touch], touches: [touch], originalEvent: event }
      for (const handler of this.touchHandlers[type]) handler(payload)
    }

    canvas.addEventListener('pointerdown', (event) => {
      if (this.activePointerId !== null) return
      event.preventDefault()
      this.activePointerId = event.pointerId
      canvas.setPointerCapture?.(event.pointerId)
      emit('start', event)
    })
    canvas.addEventListener('pointermove', (event) => {
      if (event.pointerId !== this.activePointerId) return
      event.preventDefault()
      emit('move', event)
    })
    const finishPointer = (event) => {
      if (event.pointerId !== this.activePointerId) return
      event.preventDefault()
      emit('end', event)
      canvas.releasePointerCapture?.(event.pointerId)
      this.activePointerId = null
    }
    canvas.addEventListener('pointerup', finishPointer)
    canvas.addEventListener('pointercancel', finishPointer)
  }
}

module.exports = { WebGameApi }
